import { useEffect, useMemo, useRef, useState } from 'react'
import { getFoodItems } from './menuStorage'
import { alertNewOrders } from './nativeOrderAlerts'
import { getOrders, primeOrdersCacheIfNeeded } from './orderStorage'
import {
  filterUnalertedOrders,
  markOrdersAlerted,
  pruneAlertedOrders,
  seedAlertedPendingOrders,
} from './orderAlertDedup'
import {
  buildOrdersStatusSnapshot,
  detectChimeEvents,
  type ChimePolicyContext,
} from './orderChimePolicy'
import { getLocalDeviceId } from './deviceStorage'
import { getSettings } from './settingsStorage'
import { getStationFilter } from './stationFilterStorage'
import { STATION_FILTER_CHANGED_EVENT } from './stationFilterStorage'

export const ORDERS_UPDATED_EVENT = 'wortelogy-orders-updated'

/**
 * Listen for new orders and fire chime/notification alerts.
 * Runs after login (AuthShell) so detection works before App lazy-loads.
 */
export function useOrderAlertListener(placeId: string | undefined): void {
  const [ordersTick, setOrdersTick] = useState(0)
  const [policyTick, setPolicyTick] = useState(0)
  const seededPlaceRef = useRef<string | null>(null)

  useEffect(() => {
    if (!placeId) return
    void primeOrdersCacheIfNeeded().then(() => {
      if (seededPlaceRef.current === placeId) return
      seedAlertedPendingOrders(placeId, getOrders())
      seededPlaceRef.current = placeId
    })
  }, [placeId])

  useEffect(() => {
    const bump = () => setOrdersTick((t) => t + 1)
    const bumpPolicy = () => setPolicyTick((t) => t + 1)
    window.addEventListener(ORDERS_UPDATED_EVENT, bump)
    window.addEventListener(STATION_FILTER_CHANGED_EVENT, bumpPolicy)
    window.addEventListener('wortelogy-settings-changed', bumpPolicy)
    return () => {
      window.removeEventListener(ORDERS_UPDATED_EVENT, bump)
      window.removeEventListener(STATION_FILTER_CHANGED_EVENT, bumpPolicy)
      window.removeEventListener('wortelogy-settings-changed', bumpPolicy)
    }
  }, [])

  const allOrders = useMemo(() => {
    void ordersTick
    return placeId ? getOrders() : []
  }, [ordersTick, placeId])

  const policyCtx = useMemo((): ChimePolicyContext => {
    void policyTick
    return {
      localDeviceId: getLocalDeviceId(),
      stationFilter: getStationFilter(),
      menu: getFoodItems(),
      prefs: (() => {
        const s = getSettings()
        return {
          chimeOnPendingFromThisDevice: s.chimeOnPendingFromThisDevice,
          chimeOnPendingFromCustomerApp: s.chimeOnPendingFromCustomerApp,
          chimeOnPendingFromOtherPos: s.chimeOnPendingFromOtherPos,
          chimeOnPendingFromDelivery: s.chimeOnPendingFromDelivery,
        }
      })(),
      orderModeSettings: getSettings().orderModeSettings,
    }
  }, [policyTick])

  const prevOrdersSnapshotRef = useRef<Map<string, import('../types').Order['status']>>(
    new Map(),
  )
  const snapshotReadyRef = useRef(false)

  useEffect(() => {
    prevOrdersSnapshotRef.current = new Map()
    snapshotReadyRef.current = false
    seededPlaceRef.current = null
  }, [placeId])

  useEffect(() => {
    if (!placeId) return

    pruneAlertedOrders(placeId, allOrders)

    const chimeEvents = detectChimeEvents(
      prevOrdersSnapshotRef.current,
      allOrders,
      policyCtx,
      snapshotReadyRef.current,
    )

    const unalerted = filterUnalertedOrders(placeId, chimeEvents)
    if (unalerted.length > 0) {
      void alertNewOrders(unalerted).then((didAlert) => {
        if (!didAlert) return
        markOrdersAlerted(placeId, unalerted.map((o) => o.id))
      })
    }

    prevOrdersSnapshotRef.current = buildOrdersStatusSnapshot(allOrders)
    snapshotReadyRef.current = true
  }, [allOrders, policyCtx, placeId])
}
